import * as THREE from 'three';
import { Pass, FullScreenQuad } from 'three/examples/jsm/postprocessing/Pass.js';

// LE FONDU ENCHAÎNÉ entre deux compositions, pour les titres calmes.
//
// La dernière image de la composition qui s'en va est COPIÉE, une fois, dans
// une cible à part, puis posée par-dessus la nouvelle composition, qui tourne
// déjà dessous, en s'effaçant. C'est un vrai fondu : on voit la nouvelle
// image monter à travers l'ancienne, jamais un passage par le noir (l'essai
// de fondu au noir avait été abandonné : il se lisait comme une panne).
//
// La passe vient APRÈS OutputPass : elle mélange deux images déjà encodées
// pour l'écran, ce qui est ce que l'œil compare. Elle n'est active que le
// temps d'une capture ou d'un fondu (`enabled` suit mix/capture, voir
// scene-manager.js) : le reste du temps elle ne coûte rien, et l'EffectComposer
// rend OutputPass directement à l'écran comme avant.
//
// L'ancienne image est FIGÉE pendant le fondu. Garder les deux compositions
// animées doublerait le rendu, ce que le Pi ne tient pas sur les grosses ;
// sur les 4 à 8 s d'un fondu calme, une image fixe qui s'efface se lit comme
// une rémanence.
//
// dip : une descente (le mark « fall ») creuse l'image au milieu du fondu,
// pour qu'un retrait se lise comme un retrait - l'ancien noir de deux images
// était un clignotement, pas un creux.
const blendShader = {
  uniforms: {
    tDiffuse: { value: null },
    tPrev: { value: null },
    uMix: { value: 0 },
    uDip: { value: 0 }
  },
  vertexShader: `
    varying vec2 vUv;
    void main() { vUv = uv; gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0); }
  `,
  fragmentShader: `
    uniform sampler2D tDiffuse;
    uniform sampler2D tPrev;
    uniform float uMix;
    uniform float uDip;
    varying vec2 vUv;
    void main() {
      vec4 now = texture2D(tDiffuse, vUv);
      vec4 prev = texture2D(tPrev, vUv);
      vec3 col = mix(now.rgb, prev.rgb, uMix);
      // Le creux : au plus bas quand les deux images pèsent autant.
      col *= 1.0 - uDip * 0.45 * (4.0 * uMix * (1.0 - uMix));
      gl_FragColor = vec4(col, 1.0);
    }
  `
};

const copyShader = {
  uniforms: { tDiffuse: { value: null } },
  vertexShader: blendShader.vertexShader,
  fragmentShader: `
    uniform sampler2D tDiffuse;
    varying vec2 vUv;
    void main() { gl_FragColor = texture2D(tDiffuse, vUv); }
  `
};

export class CrossfadePass extends Pass {
  constructor() {
    super();
    this.enabled = false;
    this.capture = false;   // copier l'image courante dans la cible, cette image-ci
    this.mix = 0;           // 1 = tout l'ancien, 0 = tout le nouveau
    this.dip = 0;
    this._blend = new THREE.ShaderMaterial({
      uniforms: THREE.UniformsUtils.clone(blendShader.uniforms),
      vertexShader: blendShader.vertexShader, fragmentShader: blendShader.fragmentShader,
      depthTest: false, depthWrite: false
    });
    this._copy = new THREE.ShaderMaterial({
      uniforms: THREE.UniformsUtils.clone(copyShader.uniforms),
      vertexShader: copyShader.vertexShader, fragmentShader: copyShader.fragmentShader,
      depthTest: false, depthWrite: false
    });
    this._quad = new FullScreenQuad(this._blend);
    this._prev = null;
    this._hasPrev = false;
  }

  // La cible suit la taille et le type des tampons du composer : une image
  // copiée d'un tampon demi-flottant reste demi-flottante, sans rien perdre.
  _ensure(readBuffer) {
    const w = readBuffer.width, h = readBuffer.height;
    if (this._prev && this._prev.width === w && this._prev.height === h) return;
    if (this._prev) this._prev.dispose();
    this._prev = new THREE.WebGLRenderTarget(w, h, {
      type: readBuffer.texture.type, format: THREE.RGBAFormat,
      minFilter: THREE.LinearFilter, magFilter: THREE.LinearFilter,
      depthBuffer: false, stencilBuffer: false, generateMipmaps: false
    });
    this._hasPrev = false;
  }

  render(renderer, writeBuffer, readBuffer) {
    this._ensure(readBuffer);
    if (this.capture) {
      this._copy.uniforms.tDiffuse.value = readBuffer.texture;
      this._quad.material = this._copy;
      renderer.setRenderTarget(this._prev);
      this._quad.render(renderer);
      this._hasPrev = true;
      this.capture = false;
    }
    const b = this._blend.uniforms;
    b.tDiffuse.value = readBuffer.texture;
    b.tPrev.value = this._prev.texture;
    b.uMix.value = this._hasPrev ? this.mix : 0;
    b.uDip.value = this.dip;
    this._quad.material = this._blend;
    renderer.setRenderTarget(this.renderToScreen ? null : writeBuffer);
    if (this.clear) renderer.clear();
    this._quad.render(renderer);
  }

  setSize() {
    // redimensionnée paresseusement dans render(), sur la taille réelle des tampons
  }

  dispose() {
    if (this._prev) this._prev.dispose();
    this._blend.dispose();
    this._copy.dispose();
    this._quad.dispose();
  }
}
